// SystemLogin API 工具类
// 提供便捷的 HTTP 请求方法，与 SystemLoginContext 配合使用

import { SystemLoginContextType } from '../contexts/SystemLoginContext';

export interface ApiResponse<T = any> {
  code: number;
  data?: T;
  message?: string;
}

export interface RequestOptions {
  headers?: Record<string, string>;
  timeout?: number;
}

export interface GetRequestOptions extends RequestOptions {
  params?: Record<string, any>;
}

export interface PostRequestOptions extends RequestOptions {
  data?: Record<string, any>;
  json?: any;
}

export interface DeleteRequestOptions extends RequestOptions {
  params?: Record<string, any>;
}

export interface FileUploadOptions extends RequestOptions {
  files: Record<string, File>;
}

// 业务成功码，与服务器返回保持一致
const BIZ_SUCCESS_CODE = 1;

/**
 * SystemLogin API 客户端类
 * 封装了与 SystemLoginContext 配合使用的各种 HTTP 请求方法
 */
export class SystemLoginApiClient {
  private systemLogin: SystemLoginContextType;

  constructor(systemLogin: SystemLoginContextType) {
    this.systemLogin = systemLogin;
  }

  /**
   * 发送受保护的 GET 请求
   */
  async get<T = any>(
    path: string, 
    options: GetRequestOptions = {}
  ): Promise<T> {
    const { response, isLoginNeeded } = await this.systemLogin.protectGetRequest<T>(
      path,
      options.headers,
      options.params
    );

    if (isLoginNeeded) {
      throw new Error('需要登录');
    }

    if (!response) {
      throw new Error('请求失败');
    }

    if (response.code !== BIZ_SUCCESS_CODE) {
      throw new Error(response.message || `请求失败，错误代码：${response.code}`);
    }

    return response.data as T;
  }

  /**
   * 发送受保护的 POST 请求
   */
  async post<T = any>(
    path: string, 
    options: PostRequestOptions = {}
  ): Promise<T> {
    const { response, isLoginNeeded } = await this.systemLogin.protectPostRequest<T>(
      path,
      options.headers,
      options.data,
      options.json
    );

    if (isLoginNeeded) {
      throw new Error('需要登录');
    }

    if (!response) {
      throw new Error('请求失败');
    }

    if (response.code !== BIZ_SUCCESS_CODE) {
      throw new Error(response.message || `请求失败，错误代码：${response.code}`);
    }

    return response.data as T;
  }

  /**
   * 发送受保护的 PUT 请求
   */
  async put<T = any>(
    path: string, 
    options: PostRequestOptions = {}
  ): Promise<T> {
    const { response, isLoginNeeded } = await this.systemLogin.protectPutRequest<T>(
      path,
      options.headers,
      options.data,
      options.json
    );

    if (isLoginNeeded) {
      throw new Error('需要登录');
    }

    if (!response) {
      throw new Error('请求失败');
    }

    if (response.code !== BIZ_SUCCESS_CODE) {
      throw new Error(response.message || `请求失败，错误代码：${response.code}`);
    }

    return response.data as T;
  }

  /**
   * 发送受保护的 DELETE 请求
   */
  async delete<T = any>(
    path: string, 
    options: DeleteRequestOptions = {}
  ): Promise<T> {
    const { response, isLoginNeeded } = await this.systemLogin.protectDeleteRequest<T>(
      path,
      options.headers,
      options.params
    );

    if (isLoginNeeded) {
      throw new Error('需要登录');
    }

    if (!response) {
      throw new Error('请求失败');
    }

    if (response.code !== BIZ_SUCCESS_CODE) {
      throw new Error(response.message || `请求失败，错误代码：${response.code}`);
    }

    return response.data as T;
  }

  /**
   * 发送受保护的文件上传请求
   */
  async uploadFiles<T = any>(
    path: string, 
    options: FileUploadOptions
  ): Promise<T> {
    const { response, isLoginNeeded } = await this.systemLogin.protectPostFile<T>(
      path,
      options.files,
      options.headers
    );

    if (isLoginNeeded) {
      throw new Error('需要登录');
    }

    if (!response) {
      throw new Error('请求失败');
    }

    if (response.code !== BIZ_SUCCESS_CODE) {
      throw new Error(response.message || `请求失败，错误代码：${response.code}`);
    }

    return response.data as T;
  }

  /**
   * 安全的 GET 请求 - 返回完整的响应信息
   */
  async safeGet<T = any>(
    path: string, 
    options: GetRequestOptions = {}
  ): Promise<{ data: T | null; error: string | null; isLoginNeeded: boolean }> {
    try {
      const { response, isLoginNeeded } = await this.systemLogin.protectGetRequest<T>(
        path,
        options.headers,
        options.params
      );

      if (isLoginNeeded) {
        return { data: null, error: '需要登录', isLoginNeeded: true };
      }

      if (!response) {
        return { data: null, error: '请求失败', isLoginNeeded: false };
      }

      if (response.code !== BIZ_SUCCESS_CODE) {
        return { 
          data: null, 
          error: response.message || `请求失败，错误代码：${response.code}`, 
          isLoginNeeded: false 
        };
      }

      return { data: response.data as T, error: null, isLoginNeeded: false };
    } catch (error) {
      return { 
        data: null, 
        error: error instanceof Error ? error.message : '未知错误', 
        isLoginNeeded: false 
      };
    }
  }

  /**
   * 安全的 POST 请求 - 返回完整的响应信息
   */
  async safePost<T = any>(
    path: string, 
    options: PostRequestOptions = {}
  ): Promise<{ data: T | null; error: string | null; isLoginNeeded: boolean }> {
    try {
      const { response, isLoginNeeded } = await this.systemLogin.protectPostRequest<T>(
        path,
        options.headers,
        options.data,
        options.json
      );

      if (isLoginNeeded) {
        return { data: null, error: '需要登录', isLoginNeeded: true };
      }

      if (!response) {
        return { data: null, error: '请求失败', isLoginNeeded: false };
      }

      if (response.code !== BIZ_SUCCESS_CODE) {
        return { 
          data: null, 
          error: response.message || `请求失败，错误代码：${response.code}`, 
          isLoginNeeded: false 
        };
      }

      return { data: response.data as T, error: null, isLoginNeeded: false };
    } catch (error) {
      return { 
        data: null, 
        error: error instanceof Error ? error.message : '未知错误', 
        isLoginNeeded: false 
      };
    }
  }

  /**
   * 安全的 PUT 请求 - 返回完整的响应信息
   */
  async safePut<T = any>(
    path: string, 
    options: PostRequestOptions = {}
  ): Promise<{ data: T | null; error: string | null; isLoginNeeded: boolean }> {
    try {
      const { response, isLoginNeeded } = await this.systemLogin.protectPutRequest<T>(
        path,
        options.headers,
        options.data,
        options.json
      );

      if (isLoginNeeded) {
        return { data: null, error: '需要登录', isLoginNeeded: true };
      }

      if (!response) {
        return { data: null, error: '请求失败', isLoginNeeded: false };
      }

      if (response.code !== BIZ_SUCCESS_CODE) {
        return { 
          data: null, 
          error: response.message || `请求失败，错误代码：${response.code}`, 
          isLoginNeeded: false 
        };
      }

      return { data: response.data as T, error: null, isLoginNeeded: false };
    } catch (error) {
      return { 
        data: null, 
        error: error instanceof Error ? error.message : '未知错误', 
        isLoginNeeded: false 
      };
    }
  }

  /**
   * 安全的 DELETE 请求 - 返回完整的响应信息
   */
  async safeDelete<T = any>(
    path: string, 
    options: DeleteRequestOptions = {}
  ): Promise<{ data: T | null; error: string | null; isLoginNeeded: boolean }> {
    try {
      const { response, isLoginNeeded } = await this.systemLogin.protectDeleteRequest<T>(
        path,
        options.headers,
        options.params
      );

      if (isLoginNeeded) {
        return { data: null, error: '需要登录', isLoginNeeded: true };
      }

      if (!response) {
        return { data: null, error: '请求失败', isLoginNeeded: false };
      }

      if (response.code !== BIZ_SUCCESS_CODE) {
        return { 
          data: null, 
          error: response.message || `请求失败，错误代码：${response.code}`, 
          isLoginNeeded: false 
        };
      }

      return { data: response.data as T, error: null, isLoginNeeded: false };
    } catch (error) {
      return { 
        data: null, 
        error: error instanceof Error ? error.message : '未知错误', 
        isLoginNeeded: false 
      };
    }
  }

  /**
   * 登录
   */
  async login(password: string, mobile: string, areaCode: string): Promise<void> {
    const session = await this.systemLogin.loginByPassword(password, mobile, areaCode);
    if (!session) {
      throw new Error('登录失败');
    }
  }

  /**
   * 微信登录
   */
  async loginWithWechat(code: string, appId: string, isForce: boolean = false): Promise<void> {
    const session = await this.systemLogin.loginByWechat(code, appId, isForce);
    if (!session) {
      throw new Error('微信登录失败');
    }
  }

  /**
   * Google登录
   */
  async loginWithGoogle(credential: string): Promise<void> {
    const session = await this.systemLogin.loginByGoogle(credential);
    if (!session) {
      throw new Error('Google登录失败');
    }
  }

  /**
   * 发送OTP验证码
   */
  async sendOtp(msgContent: string): Promise<void> {
    await this.systemLogin.extractMobileInfoAndSendOtp(msgContent);
  }

  /**
   * 登出
   */
  logout(): boolean {
    return this.systemLogin.clearLoginSession();
  }

  /**
   * 检查登录状态
   */
  isLoggedIn(): boolean {
    return !this.systemLogin.checkIsLoginNeeded();
  }

  /**
   * 获取服务器时间
   */
  async getServerTime(): Promise<string> {
    return await this.systemLogin.getServerSideCurrentTimeInShanghaiTimezone();
  }

  /**
   * 获取或创建最新的用户聊天会话
   */
  async getUserChatSession(): Promise<any> {
    return await this.systemLogin.getOrCreateLatestUserChatSession();
  }

  /**
   * 调用 describeImages 接口
   */
  async describeImages(forComposeVideoFileIds: string[], defaultLanguage?: string): Promise<any> {
    return await this.systemLogin.describeImages(forComposeVideoFileIds, defaultLanguage);
  }

  /**
   * 调用 generateImages 接口
   */
  async generateImages(userMessage: string, imageUrl?: string): Promise<any> {
    return await this.systemLogin.generateImages(userMessage, imageUrl);
  }

  // ============ 用户地址相关接口 ============
  /**
   * 安全查询用户地址列表
   */
  async safeGetUserAddressList<T = any>(cityId: string): Promise<{ data: T[] | null; error: string | null; isLoginNeeded: boolean }> {
    const path = `app-api/userAddr/list?cityId=${cityId}`;
    return await this.safeGet<T[]>(path);
  }

  /**
   * 安全查看用户地址详情
   */
  async safeGetUserAddressById<T = any>(addrId: string): Promise<{ data: T | null; error: string | null; isLoginNeeded: boolean }> {
    const path = `app-api/userAddr/view/${addrId}`;
    return await this.safeGet<T>(path);
  }

  /**
   * 安全添加用户地址
   */
  async safeAddUserAddress<T = any>(addressData: T): Promise<{ data: T | null; error: string | null; isLoginNeeded: boolean }> {
    return await this.safePost<T>('app-api/userAddr/add', { json: addressData });
  }

  /**
   * 安全更新用户地址 (使用PUT请求)
   */
  async safeUpdateUserAddress<T = any>(addressData: T): Promise<{ data: T | null; error: string | null; isLoginNeeded: boolean }> {
    return await this.safePut<T>('app-api/userAddr/update', { json: addressData });
  }
}

/**
 * 创建 API 客户端实例的 Hook
 */
export const createSystemLoginApiClient = (systemLogin: SystemLoginContextType): SystemLoginApiClient => {
  return new SystemLoginApiClient(systemLogin);
};

/**
 * 常用的API路径常量
 */
export const API_PATHS = {
  // 用户相关
  USER: {
    LOGIN: 'app-api/user/loginByPassword/mobile',
    SEND_OTP: 'app-api/user/sendOtpByAiQuestion',
    PROFILE: 'app-api/user/profile',
    UPDATE_PROFILE: 'app-api/user/updateProfile',
    WECHAT_LOGIN: 'app-api/user/loginByWechatAuth',
    GOOGLE_LOGIN: 'app-api/user/loginByGoogleAuth',
    ADDRESS_ADD: 'app-api/userAddr/add',
    ADDRESS_UPDATE: 'app-api/userAddr/update',
    ADDRESS_VIEW: 'app-api/userAddr/view/{addrId}',
    ADDRESS_LIST: 'app-api/userAddr/list?cityId={cityId}',
  },
  
  // AI相关
  AI: {
    GENERATE_VIDEO: 'app-api/ai/generateVideo',
    GET_CURRENT_TIME: 'app-api/ai/agent/getCurrentTimeInShanghaiTimezone',
    GET_OR_CREATE_USER_SESSION: 'app-api/ai/agent/getOrCreateLatestAdTaskUserSession',
    START_VIDEO_EDITING: 'app-api/ai/agent/startVideoEditing',
    DESCRIBE_IMAGES: 'app-api/ai/agent/describeImages',
    GENERATE_IMAGES: 'app-api/ai/agent/startToyImageGeneration',
    UPLOAD_IMAGES: 'app-api/ai/agent/uploadToyDesignedImage',
    GENERATE_IMAGE_HISTORY: 'app-api/ai/agent/queryGeneratedToyImageChatHistory',
    IMAGE_LIST: 'app-api/ai/agent/queryGeneratedToyImageList',
    SUBMIT_DESIGN: 'app-api/ai/agent/submitToyDesignedImage',
    SUBMIT_DESIGN_HISTORY: 'app-api/ai/agent/querySubmittedToyImageHistory',
  },
  
  // 文件上传
  UPLOAD: {
    IMAGE: 'app-api/upload/image',
    VIDEO: 'app-api/upload/video',
    DOCUMENT: 'app-api/upload/document',
    FILE_STORE: 'app-api/upload/file/store',
  },
  
  // 其他
  COMMON: {
    HEALTH_CHECK: 'app-api/health',
    VERSION: 'app-api/version',
  }
} as const;

export default SystemLoginApiClient;